/**
 * HelpPanel Component
 * LAYER-SHELL: Unified help panel
 *
 * Slide-out help panel with FAQ content and support links.
 * Triggered from TopBar help button.
 */
import React, { useEffect, useRef } from 'react';
import { XMarkIcon, ChatBubbleLeftRightIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import './HelpPanel.scss';

export interface FaqItem {
  question: string;
  answer: string;
}

export interface ContactLink {
  type: 'email' | 'chat';
  label: string;
  detail: string;
  href: string;
}

interface HelpPanelProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * FAQ items to display. Uses defaults if not provided.
   */
  faqItems?: FaqItem[];
  /**
   * Contact links to display. Uses defaults if not provided.
   */
  contactLinks?: ContactLink[];
}

// Default FAQ content
const defaultFaqItems: FaqItem[] = [
  {
    question: 'What is a signature guarantee?',
    answer: 'A signature guarantee is a form of authentication that verifies the legitimacy of a signature and the identity of the signer. It is commonly required for transferring securities.',
  },
  {
    question: 'How long does the process take?',
    answer: 'Standard requests are processed within 5 business days. Expedited and Rush options are available at checkout for faster turnaround. Complex transfers may take longer depending on documentation.',
  },
  {
    question: 'What documents do I need?',
    answer: 'Required documents vary by transfer type. Typically you will need government-issued ID, the original certificate or statement, and transfer request form.',
  },
];

const defaultContactLinks: ContactLink[] = [
  {
    type: 'email',
    label: 'Email Support',
    detail: 'support@esignatureguarantee.com',
    href: 'mailto:support@esignatureguarantee.com',
  },
  {
    type: 'chat',
    label: 'Live Chat',
    detail: 'Available Mon-Fri, 9am-5pm ET',
    href: '#',
  },
];

export const HelpPanel: React.FC<HelpPanelProps> = ({
  isOpen,
  onClose,
  faqItems = defaultFaqItems,
  contactLinks = defaultContactLinks,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Focus trap and escape key handling
  useEffect(() => {
    if (!isOpen) return;

    const panel = panelRef.current;
    if (!panel) return;

    // Get all focusable elements within the panel
    const getFocusableElements = (): HTMLElement[] => {
      const elements = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      return Array.from(elements);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key === 'Tab') {
        const focusableElements = getFocusableElements();
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          if (document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    closeButtonRef.current?.focus();
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <>
      {/* Overlay */}
      <div
        className={`help-panel-overlay ${isOpen ? 'help-panel-overlay--visible' : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        className={`help-panel ${isOpen ? 'help-panel--open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Help and FAQ"
        aria-hidden={!isOpen}
      >
        <div className="help-panel__header">
          <h2 className="help-panel__title">Help & FAQ</h2>
          <button
            ref={closeButtonRef}
            className="help-panel__close-btn"
            onClick={onClose}
            aria-label="Close help panel"
          >
            <XMarkIcon className="help-panel__close-icon" />
          </button>
        </div>

        <div className="help-panel__content">
          {/* FAQ Section */}
          <section className="help-panel__section">
            <h3 className="help-panel__section-title">Frequently Asked Questions</h3>
            <div className="help-panel__faq-list">
              {faqItems.map((item, index) => (
                <details key={index} className="help-panel__faq-item">
                  <summary className="help-panel__faq-question">
                    {item.question}
                  </summary>
                  <p className="help-panel__faq-answer">{item.answer}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section className="help-panel__section">
            <h3 className="help-panel__section-title">Need More Help?</h3>
            <div className="help-panel__contact-options">
              {contactLinks.map((link, index) => {
                const Icon = link.type === 'email' ? EnvelopeIcon : ChatBubbleLeftRightIcon;
                return (
                  <a
                    key={index}
                    href={link.href}
                    className="help-panel__contact-link"
                  >
                    <Icon className="help-panel__contact-icon" />
                    <div className="help-panel__contact-text">
                      <span className="help-panel__contact-label">{link.label}</span>
                      <span className="help-panel__contact-detail">{link.detail}</span>
                    </div>
                  </a>
                );
              })}
            </div>
          </section>
        </div>
      </aside>
    </>
  );
};

export default HelpPanel;
